/**
 * Shared light/dark scope for Next Steps (Courses) surfaces.
 *
 * The palette lives in `src/styles/_courses-theme.scss` as CSS custom properties
 * under `.courses-theme` / `.courses-theme.courses-dark`. Bind `themeClass` on a
 * component's root to make those variables resolve inside it.
 *
 * This matters most for anything that Teleports out of the page — modals,
 * sheets, toasts land under <body>/<ion-app>, outside the page element carrying
 * the theme scope, so the variables would otherwise never reach them and they'd
 * fall back to the light defaults while the page behind them is dark.
 *
 * The flag itself is Vuex state (courses/isDark), not page-local data, so it is
 * consistent across every Next Steps screen.
 */
import themeScope from '@/directives/theme-scope';

export default {
  directives: { themeScope },
  computed: {
    // Read defensively rather than via mapGetters: this mixin is applied to
    // low-level presentational components (HighlightDrawer and friends) that are
    // mounted standalone in unit tests with no store installed, and mapGetters
    // throws on a missing store. Styling must never be able to break a component.
    isDark() {
      return !!this.$store?.getters?.['courses/isDark'];
    },
    // For plain divs this component renders and nothing else writes to. On any
    // element Ionic owns — an ion-page / master-layout root, or an <ion-modal> —
    // use `v-theme-scope="themeScope"` instead: a reactive :class rewrite there
    // wipes Ionic's runtime classes (ion-page-hidden on a cached page,
    // overlay-hidden on a dormant modal), which un-hides them over the current
    // screen and eats every tap. See directives/theme-scope.js.
    themeClass() {
      return this.isDark ? 'courses-theme courses-dark' : 'courses-theme';
    },
    themeScope() {
      return { base: 'courses-theme', dark: 'courses-dark', isDark: this.isDark };
    },
  },
};
